import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Upload, Loader2, Download, Trash2, FileText, ChevronDown, ChevronUp, RefreshCw, Building2, MapPin, Clock, Send, Mail, DollarSign, CalendarClock, Pencil, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutomatedRenewals() {
  var { token, user } = useAuth();
  var [batches, setBatches] = useState([]);
  var [selectedBatch, setSelectedBatch] = useState(null);
  var [batchDetail, setBatchDetail] = useState(null);
  var [uploading, setUploading] = useState(false);
  var [uploadingBills, setUploadingBills] = useState(false);
  var [billDragOver, setBillDragOver] = useState(false);
  var [loading, setLoading] = useState(false);
  var [generating, setGenerating] = useState({});
  var [emailing, setEmailing] = useState({});
  var [expandedGroups, setExpandedGroups] = useState({});
  var [commissions, setCommissions] = useState({}); // per-group: { idx: { type: 'percent', value: '' } }
  var [confirmDeleteBatch, setConfirmDeleteBatch] = useState(null);
  var [bulkCommission, setBulkCommission] = useState({ type: 'percent', value: '' });
  var [bulkGenerating, setBulkGenerating] = useState(false);
  var [bulkProgress, setBulkProgress] = useState({ progress: 0, total: 0 });
  var emptyRates = { y1_peak: '', y1_shoulder: '', y1_offpeak: '', y2_peak: '', y2_shoulder: '', y2_offpeak: '', y3_peak: '', y3_shoulder: '', y3_offpeak: '', metering_monthly: '', service_monthly: '', term_months: '', connection_type: '', quote_valid_until: '', start_date: '', solar_size: '', has_battery: '', battery_size: '' };
  var yearFields = ['y1_peak', 'y1_shoulder', 'y1_offpeak', 'y2_peak', 'y2_shoulder', 'y2_offpeak', 'y3_peak', 'y3_shoulder', 'y3_offpeak'];
  var [customRates, setCustomRates] = useState({}); // per-group draft values
  var [savingRates, setSavingRates] = useState({});
  var [bulkRates, setBulkRates] = useState(emptyRates);
  var [applyingBulkRates, setApplyingBulkRates] = useState(false);
  var [selectedStates, setSelectedStates] = useState([]);

  function groupState(g) { return String((g && g.state_raw) || '').toUpperCase() || 'N/A'; }
  function availableStates() {
    var groups = (batchDetail && batchDetail.groups) || [];
    var seen = {};
    groups.forEach(function(g) { seen[groupState(g)] = true; });
    return Object.keys(seen).sort();
  }
  function visibleItems() {
    var groups = (batchDetail && batchDetail.groups) || [];
    return groups.map(function(g, i) { return { grp: g, idx: i }; }).filter(function(o) { return selectedStates.length === 0 || selectedStates.indexOf(groupState(o.grp)) !== -1; });
  }
  function toggleStateFilter(s) {
    setSelectedStates(function(prev) {
      return prev.indexOf(s) !== -1 ? prev.filter(function(x) { return x !== s; }) : prev.concat([s]);
    });
  }
  function statesParam() {
    return selectedStates.length > 0 ? '&states=' + encodeURIComponent(selectedStates.join(',')) : '';
  }

  var fetchBatches = useCallback(function() {
    axios.get(API_URL + '/api/ci-renewals?authorization=' + token)
      .then(function(r) { setBatches(r.data); })
      .catch(function() {});
  }, [token]);

  useEffect(function() { fetchBatches(); }, [fetchBatches]);

  function handleUpload(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    var fd = new FormData();
    fd.append('file', file);
    axios.post(API_URL + '/api/ci-renewals/upload?authorization=' + token, fd)
      .then(function(r) {
        toast.success('Uploaded! Found ' + r.data.total_groups + ' customer group(s) from ' + r.data.total_rows + ' row(s)');
        fetchBatches();
        setSelectedBatch(r.data.id);
        setBatchDetail(r.data);
        setSelectedStates([]);
        seedCustomRates(r.data);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Upload failed');
      })
      .finally(function() { setUploading(false); e.target.value = ''; });
  }

  function seedCustomRates(batch) {
    var saved = batch.custom_rates || {};
    var seeded = {};
    Object.keys(saved).forEach(function(k) {
      var s = {};
      Object.keys(emptyRates).forEach(function(f) { s[f] = saved[k][f] || ''; });
      var hasYear = yearFields.some(function(f) { return s[f]; });
      if (!hasYear) {
        ['peak', 'shoulder', 'offpeak'].forEach(function(fld) {
          if (saved[k][fld]) { s['y1_' + fld] = saved[k][fld]; s['y2_' + fld] = saved[k][fld]; s['y3_' + fld] = saved[k][fld]; }
        });
      }
      seeded[k] = s;
    });
    setCustomRates(seeded);
  }

  function handleBillFiles(fileList) {
    var files = Array.prototype.slice.call(fileList || []).filter(function(f) {
      return /\.(pdf|jpg|jpeg|png)$/i.test(f.name);
    });
    if (files.length === 0) { toast.error('Please drop PDF or image bills'); return; }
    setUploadingBills(true);
    var fd = new FormData();
    files.forEach(function(f) { fd.append('files', f); });
    axios.post(API_URL + '/api/ci-renewals/upload-bills?authorization=' + token, fd)
      .then(function(r) {
        var failed = (r.data.bill_results || []).filter(function(x) { return !x.success; });
        if (failed.length > 0) {
          toast.warning(failed.length + ' bill(s) could not be read: ' + failed.map(function(x) { return x.filename; }).join(', '));
        }
        toast.success('Extracted ' + r.data.total_groups + ' customer contract(s) from ' + files.length + ' bill(s)');
        fetchBatches();
        setSelectedBatch(r.data.id);
        setBatchDetail(r.data);
        setSelectedStates([]);
        seedCustomRates(r.data);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Bill extraction failed');
      })
      .finally(function() { setUploadingBills(false); setBillDragOver(false); });
  }

  function viewBatch(id) {
    setLoading(true);
    if (selectedBatch !== id) setSelectedStates([]);
    setSelectedBatch(id);
    axios.get(API_URL + '/api/ci-renewals/' + id + '?authorization=' + token)
      .then(function(r) { setBatchDetail(r.data); seedCustomRates(r.data); })
      .catch(function() { toast.error('Failed to load batch'); })
      .finally(function() { setLoading(false); });
  }

  function deleteBatch(id) {
    setConfirmDeleteBatch(id);
  }
  function confirmDeleteBatchAction() {
    var id = confirmDeleteBatch;
    setConfirmDeleteBatch(null);
    if (!id) return;
    axios.delete(API_URL + '/api/ci-renewals/' + id + '?authorization=' + token)
      .then(function() {
        toast.success('Batch deleted');
        fetchBatches();
        if (selectedBatch === id) { setSelectedBatch(null); setBatchDetail(null); }
      })
      .catch(function() { toast.error('Delete failed'); });
  }

  function generatePdf(batchId, groupIdx) {
    setGenerating(function(prev) { var n = {...prev}; n[groupIdx] = true; return n; });
    var comm = commissions[groupIdx] || {};
    var commType = comm.type || '';
    var commValue = comm.value || '';
    var url = API_URL + '/api/ci-renewals/' + batchId + '/generate-pdf/' + groupIdx + '?authorization=' + token;
    if (commType && commValue) {
      url += '&commission_type=' + encodeURIComponent(commType) + '&commission_value=' + encodeURIComponent(commValue);
    }
    axios.post(url, {}, { responseType: 'blob' })
      .then(function(r) {
        var url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        var a = document.createElement('a');
        var disp = r.headers['content-disposition'] || '';
        var fn = disp.includes('filename=') ? disp.split('filename="')[1].split('"')[0] : 'contract.pdf';
        a.href = url; a.download = fn; a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF generated and downloaded');
        // Refresh batch to show generated status
        viewBatch(batchId);
      })
      .catch(function() { toast.error('PDF generation failed'); })
      .finally(function() { setGenerating(function(prev) { var n = {...prev}; n[groupIdx] = false; return n; }); });
  }

  function downloadPdf(batchId, groupIdx, grp) {
    var company = (grp && grp.company_name) || 'Contract';
    var nmi = (grp && grp.supply_addresses && grp.supply_addresses[0] && grp.supply_addresses[0].nmi) || '';
    var fileName = nmi ? company + ' - ' + nmi + '.pdf' : company + '.pdf';
    axios.get(API_URL + '/api/ci-renewals/' + batchId + '/download-pdf/' + groupIdx + '?authorization=' + token, { responseType: 'blob' })
      .then(function(r) {
        var blob = new Blob([r.data], { type: 'application/pdf' });
        var a = document.createElement('a'); a.href = window.URL.createObjectURL(blob); a.download = fileName; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(a.href);
      })
      .catch(function() { toast.error('Download failed'); });
  }

  function emailContract(batchId, groupIdx, grp) {
    var email = grp.email || '';
    if (!email || email.indexOf('@') === -1) {
      toast.error('No valid email address for ' + grp.company_name);
      return;
    }
    if (!window.confirm('Send renewal email to ' + email + '?\n\nThe contract PDF will be attached. Email type will be auto-selected based on contract end date.')) return;
    setEmailing(function(prev) { var n = {...prev}; n[groupIdx] = true; return n; });
    var fd = new FormData();
    fd.append('rep_phone', '03 8535 1300');
    axios.post(API_URL + '/api/ci-renewals/' + batchId + '/email/' + groupIdx + '?authorization=' + token, fd)
      .then(function(r) {
        toast.success('Email sent to ' + email + ' (' + (r.data.type || '') + ' template)');
        viewBatch(batchId);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to send email');
      })
      .finally(function() { setEmailing(function(prev) { var n = {...prev}; n[groupIdx] = false; return n; }); });
  }

  async function handleMailto(batchId, groupIdx, grp) {
    var email = grp.email || '';
    var company = grp.company_name || '';
    var contact = grp.contact_name || '';

    // Download PDF first (same pattern as working Comparison Tool)
    var pdfGenerated = batchDetail && batchDetail.generated_pdfs && batchDetail.generated_pdfs[String(groupIdx)];
    if (pdfGenerated) {
      try {
        var res = await axios.get(API_URL + '/api/ci-renewals/' + batchId + '/download-pdf/' + groupIdx + '?authorization=' + token, { responseType: 'blob' });
        var blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        var link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', 'NBE_Renewal_' + (company || 'contract').replace(/\s+/g, '_') + '.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error('PDF download failed:', err);
      }
    } else {
      // Generate first, then download
      try {
        setGenerating(function(prev) { var n = {...prev}; n[groupIdx] = true; return n; });
        var comm = commissions[groupIdx] || {};
        var url = API_URL + '/api/ci-renewals/' + batchId + '/generate-pdf/' + groupIdx + '?authorization=' + token;
        if (comm.type && comm.value) {
          url += '&commission_type=' + encodeURIComponent(comm.type) + '&commission_value=' + encodeURIComponent(comm.value);
        }
        var res2 = await axios.post(url, {}, { responseType: 'blob' });
        var blobUrl2 = window.URL.createObjectURL(new Blob([res2.data], { type: 'application/pdf' }));
        var link2 = document.createElement('a');
        var disp = res2.headers['content-disposition'] || '';
        var fn = disp.includes('filename=') ? disp.split('filename="')[1].split('"')[0] : 'NBE_Renewal_' + (company || 'contract').replace(/\s+/g, '_') + '.pdf';
        link2.href = blobUrl2;
        link2.setAttribute('download', fn);
        document.body.appendChild(link2);
        link2.click();
        link2.remove();
        viewBatch(batchId);
      } catch (err) {
        console.error('PDF generation failed:', err);
        toast.error('PDF generation failed');
      } finally {
        setGenerating(function(prev) { var n = {...prev}; n[groupIdx] = false; return n; });
      }
    }

    // Build email with contract details
    var subject = 'Next Business Energy - C&I Renewal Agreement' + (company ? ' - ' + company : '');
    var bodyLines = [
      'Hi' + (contact ? ' ' + contact : '') + ',',
      '',
      'Please find attached your C&I Energy Renewal Agreement from Next Business Energy.',
      '',
      '--- Contract Details ---',
      'Company: ' + (company || 'N/A'),
      'ABN: ' + (grp.abn || 'N/A'),
      'Contact: ' + (contact || 'N/A'),
      'State: ' + (grp.contract_state || 'N/A'),
      'Transfer Date: ' + (grp.proposed_transfer_date || 'N/A'),
    ];
    var comm2 = commissions[groupIdx] || {};
    if (comm2.type && comm2.value) {
      bodyLines.push('Commission: ' + comm2.value + (comm2.type === 'percent' ? '%' : ' c/kWh'));
    }
    bodyLines.push('', 'Please review and sign where indicated.', '', 'Please attach the downloaded PDF to this email.', '', 'Kind regards,', 'Next Business Energy');

    var mailto = 'mailto:' + email + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(bodyLines.join('\n'));
    window.location.href = mailto;
    toast.success('PDF downloaded. Please attach it to the email.');
  }

  function updateCommission(idx, key, val) {
    setCommissions(function(prev) {
      var n = { ...prev };
      n[idx] = { ...(n[idx] || { type: 'percent', value: '' }), [key]: val };
      return n;
    });
  }

  function updateCustomRate(idx, key, val) {
    setCustomRates(function(prev) {
      var n = { ...prev };
      n[idx] = { ...(n[idx] || emptyRates), [key]: val };
      return n;
    });
  }

  function hasAnyRateValue(r) {
    if (!r) return false;
    return Object.keys(r).some(function(k) { return String(r[k] || '').trim() !== ''; });
  }

  function saveCustomRates(idx) {
    var rates = customRates[idx] || emptyRates;
    if (!hasAnyRateValue(rates)) { toast.error('Enter at least one custom rate value'); return; }
    setSavingRates(function(prev) { var n = {...prev}; n[idx] = true; return n; });
    axios.put(API_URL + '/api/ci-renewals/' + batchDetail.id + '/custom-rates/' + idx + '?authorization=' + token, rates)
      .then(function() {
        toast.success('Custom rates saved. Environmental charges will still come from the rate sheet.');
        viewBatch(batchDetail.id);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to save custom rates');
      })
      .finally(function() { setSavingRates(function(prev) { var n = {...prev}; n[idx] = false; return n; }); });
  }

  function clearCustomRates(idx) {
    axios.delete(API_URL + '/api/ci-renewals/' + batchDetail.id + '/custom-rates/' + idx + '?authorization=' + token)
      .then(function() {
        toast.success('Custom rates cleared — rate sheet pricing will be used');
        setCustomRates(function(prev) { var n = {...prev}; delete n[idx]; return n; });
        viewBatch(batchDetail.id);
      })
      .catch(function() { toast.error('Failed to clear custom rates'); });
  }

  function applyRatesToAll() {
    if (!batchDetail) return;
    if (!hasAnyRateValue(bulkRates)) { toast.error('Enter at least one custom rate value'); return; }
    var count = visibleItems().length;
    var scope = selectedStates.length > 0 ? count + ' customer(s) in ' + selectedStates.join(', ') : 'ALL ' + count + ' customer(s)';
    if (!window.confirm('Apply these custom rates to ' + scope + ' in this batch? Environmental charges will still come from the rate sheet.')) return;
    setApplyingBulkRates(true);
    axios.post(API_URL + '/api/ci-renewals/' + batchDetail.id + '/custom-rates/apply-all?authorization=' + token + statesParam(), bulkRates)
      .then(function(r) {
        toast.success(r.data.message || 'Custom rates applied to all customers');
        viewBatch(batchDetail.id);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to apply rates');
      })
      .finally(function() { setApplyingBulkRates(false); });
  }

  function generateAllPdfs() {
    if (!batchDetail) return;
    setBulkGenerating(true);
    setBulkProgress({ progress: 0, total: visibleItems().length });
    var url = API_URL + '/api/ci-renewals/' + batchDetail.id + '/generate-all?authorization=' + token + statesParam();
    if (bulkCommission.type && bulkCommission.value) {
      url += '&commission_type=' + encodeURIComponent(bulkCommission.type) + '&commission_value=' + encodeURIComponent(bulkCommission.value);
    }
    axios.post(url)
      .then(function(r) {
        toast.success('Bulk generation started for ' + (r.data.total || 0) + ' contracts');
        pollBulkProgress();
      })
      .catch(function() { toast.error('Failed to start bulk generation'); setBulkGenerating(false); });
  }

  function pollBulkProgress() {
    if (!batchDetail) return;
    var pollUrl = API_URL + '/api/ci-renewals/' + batchDetail.id + '/generate-all/status?authorization=' + token;
    var done = false;
    var inFlight = false;
    var failures = 0;
    var interval = setInterval(function() {
      if (done || inFlight) return;
      inFlight = true;
      axios.get(pollUrl)
        .then(function(r) {
          failures = 0;
          if (done) return;
          var d = r.data;
          setBulkProgress({ progress: d.progress || 0, total: d.total || 0 });
          if (d.status === 'complete') {
            done = true;
            clearInterval(interval);
            setBulkGenerating(false);
            var errCount = (d.errors || []).length;
            if (errCount > 0) {
              toast.success('Generation complete (' + errCount + ' errors). Ready to download.');
            } else {
              toast.success('All PDFs generated! Ready to download.');
            }
            viewBatch(batchDetail.id);
          }
        })
        .catch(function() {
          failures += 1;
          if (failures >= 5 && !done) {
            done = true;
            clearInterval(interval);
            setBulkGenerating(false);
            toast.error('Lost connection to generation status');
          }
        })
        .finally(function() { inFlight = false; });
    }, 2000);
  }

  function downloadAllPdfs() {
    if (!batchDetail) return;
    var url = API_URL + '/api/ci-renewals/' + batchDetail.id + '/download-all?authorization=' + token + statesParam();
    axios.get(url, { responseType: 'blob' })
      .then(function(r) {
        var blob = new Blob([r.data], { type: 'application/zip' });
        var a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        var disp = r.headers['content-disposition'] || '';
        var fn = disp.includes('filename=') ? disp.split('filename="')[1].split('"')[0] : 'Renewals.zip';
        a.download = fn;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(a.href);
        toast.success('All PDFs downloaded');
      })
      .catch(function() { toast.error('Download failed — generate PDFs first'); });
  }

  function hasMixedStateGroups() {
    var groups = (batchDetail && batchDetail.groups) || [];
    return groups.some(function(g) {
      var sts = {};
      (g.supply_addresses || []).forEach(function(sa) { var s = String(sa.state || '').toUpperCase(); if (s) sts[s] = 1; });
      return Object.keys(sts).length > 1;
    });
  }

  function regroupBatch() {
    if (!batchDetail) return;
    if (!window.confirm('Re-split this batch by customer + state?\n\nCustomers with sites in multiple states will be split into one contract per state. Any generated PDFs and saved custom rates for this batch will be cleared.')) return;
    setLoading(true);
    axios.post(API_URL + '/api/ci-renewals/' + batchDetail.id + '/regroup?authorization=' + token)
      .then(function(r) {
        toast.success('Batch re-split into ' + (r.data.total_groups || 0) + ' contract(s) by customer + state');
        setBatchDetail(r.data);
        setSelectedStates([]);
        seedCustomRates(r.data);
        fetchBatches();
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Re-split failed');
      })
      .finally(function() { setLoading(false); });
  }

  function toggleGroup(idx) {
    setExpandedGroups(function(prev) { var n = {...prev}; n[idx] = !n[idx]; return n; });
  }

  var stateLabel = function(s) {
    return s === 'QLD_SA' ? 'QLD/SA' : s;
  };

  return (
    <Layout>
    <div className="space-y-6" data-testid="renewals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">C&I Automated Renewals</h1>
          <p className="text-sm text-slate-500 mt-1">Upload customer data to bulk-generate C&I contract PDFs</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Upload + Batches */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Upload className="w-5 h-5" />Upload Customer Data</CardTitle>
              <CardDescription>Upload Excel with customer NMIs, addresses, ABNs etc.</CardDescription>
            </CardHeader>
            <CardContent>
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 hover:border-[#005394] transition-colors" data-testid="renewal-upload-area">
                {uploading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin" />Parsing customer data...</div>
                ) : (
                  <div className="flex flex-col items-center text-sm text-slate-500">
                    <Upload className="w-6 h-6 mb-1 text-[#005394]" />
                    <span className="font-medium">Click to upload .xlsx</span>
                  </div>
                )}
                <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="renewal-file-input" />
              </label>
              <label
                className={'mt-3 flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ' + (billDragOver ? 'border-[#8CC63F] bg-[#8CC63F]/10' : 'border-slate-300 hover:bg-slate-50 hover:border-[#8CC63F]')}
                onDragOver={function(e) { e.preventDefault(); setBillDragOver(true); }}
                onDragLeave={function() { setBillDragOver(false); }}
                onDrop={function(e) { e.preventDefault(); if (!uploadingBills) handleBillFiles(e.dataTransfer.files); }}
                data-testid="bill-upload-area"
              >
                {uploadingBills ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin" />Reading bills with AI...</div>
                ) : (
                  <div className="flex flex-col items-center text-sm text-slate-500 px-2 text-center">
                    <FileText className="w-6 h-6 mb-1 text-[#8CC63F]" />
                    <span className="font-medium">Drag &amp; drop PDF bills here</span>
                    <span className="text-xs text-slate-400">AI extracts customer &amp; site details (multiple allowed)</span>
                  </div>
                )}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={function(e) { handleBillFiles(e.target.files); e.target.value = ''; }} className="hidden" disabled={uploadingBills} data-testid="bill-file-input" />
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" />Upload Batches</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {batches.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No batches yet</p>}
              {batches.map(function(b) {
                var isSelected = selectedBatch === b.id;
                return (
                  <div key={b.id} onClick={function() { viewBatch(b.id); }} className={'flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ' + (isSelected ? 'bg-[#005394]/5 border-[#005394] shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50')} data-testid={'batch-' + b.id}>
                    <div className="min-w-0 flex-1">
                      <p className={'text-sm font-medium truncate ' + (isSelected ? 'text-[#005394]' : 'text-slate-700')}>{b.filename}</p>
                      <p className="text-xs text-slate-400">{new Date(b.uploaded_at).toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{b.total_groups} customer(s), {b.total_rows} NMI(s)</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0 ml-2" onClick={function(e) { e.stopPropagation(); deleteBatch(b.id); }} data-testid={'delete-batch-' + b.id}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Right: Customer Groups */}
        <div className="lg:col-span-2">
          {!batchDetail ? (
            <Card><CardContent className="py-16 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Select or upload a batch to view customers</p></CardContent></Card>
          ) : loading ? (
            <Card><CardContent className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#005394] mx-auto" /></CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="customer-groups">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">{batchDetail.filename}</h2>
                  <p className="text-xs text-slate-500">{batchDetail.total_groups} customer group(s) from {batchDetail.total_rows} row(s) - Uploaded {new Date(batchDetail.uploaded_at).toLocaleString()}</p>
                </div>
                <Button variant="outline" size="sm" onClick={function() { viewBatch(batchDetail.id); }} data-testid="refresh-batch">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
                </Button>
              </div>

              {/* Mixed-state warning */}
              {hasMixedStateGroups() && (
                <Card className="mb-3 border-amber-300 bg-amber-50" data-testid="mixed-state-warning">
                  <CardContent className="py-2.5 px-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-xs text-amber-800 flex-1 min-w-[240px]">
                        <span className="font-semibold">This batch has customers with sites in multiple states grouped together.</span> Pricing differs by state — re-split so each customer gets one contract per state.
                      </p>
                      <Button size="sm" onClick={regroupBatch} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="regroup-btn">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />Re-split by State
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* State Filter */}
              <Card className="mb-3" data-testid="state-filter">
                <CardContent className="py-2.5 px-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <MapPin className="w-4 h-4 text-[#005394]" />
                    <span className="text-xs font-semibold text-[#005394]">Filter by State:</span>
                    {availableStates().map(function(s) {
                      var isActive = selectedStates.indexOf(s) !== -1;
                      var count = ((batchDetail.groups || []).filter(function(g) { return groupState(g) === s; })).length;
                      return <button key={s} onClick={function() { toggleStateFilter(s); }} className={'text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ' + (isActive ? 'bg-[#005394] text-white border-[#005394]' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50')} data-testid={'state-chip-' + s}>
                        {s} ({count})
                      </button>;
                    })}
                    {selectedStates.length > 0 && (
                      <button onClick={function() { setSelectedStates([]); }} className="text-xs px-2 py-1 text-slate-500 underline hover:text-slate-700" data-testid="state-filter-clear">
                        Show All
                      </button>
                    )}
                    <span className="text-xs text-slate-400 ml-auto" data-testid="state-filter-count">{visibleItems().length} of {(batchDetail.groups || []).length} customer(s) shown</span>
                  </div>
                  {selectedStates.length > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1">Generate All, Download All and Apply-to-All rates will only include {selectedStates.join(', ')} customers.</p>
                  )}
                </CardContent>
              </Card>

              {/* Bulk Controls */}
              <Card className="mb-3 border-[#005394]/20 bg-[#005394]/[0.02]" data-testid="bulk-controls">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#005394]" />
                      <span className="text-xs font-semibold text-[#005394]">Bulk Commission:</span>
                      <div className="flex gap-1">
                        {[['percent', '%'], ['ckwh', 'c/kWh']].map(function(ct) {
                          var isActive = bulkCommission.type === ct[0];
                          return <button key={ct[0]} onClick={function() { setBulkCommission(function(p) { return {...p, type: ct[0]}; }); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-[#005394] text-white border-[#005394]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'bulk-comm-type-' + ct[0]}>
                            {ct[1]}
                          </button>;
                        })}
                      </div>
                      <Input className="h-7 text-xs w-20" value={bulkCommission.value} onChange={function(e) { setBulkCommission(function(p) { return {...p, value: e.target.value}; }); }} placeholder="e.g. 5" data-testid="bulk-comm-value" />
                      {bulkCommission.type && bulkCommission.value && (
                        <span className="text-xs text-[#005394] font-medium">+{bulkCommission.value}{bulkCommission.type === 'percent' ? '%' : ' c/kWh'}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <Button size="sm" onClick={generateAllPdfs} disabled={bulkGenerating} className="bg-[#005394] hover:bg-[#005394]/90" data-testid="generate-all-btn">
                        {bulkGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                        {bulkGenerating ? 'Generating ' + bulkProgress.progress + '/' + bulkProgress.total + '...' : 'Generate All PDFs'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={downloadAllPdfs} data-testid="download-all-btn">
                        <Download className="w-3.5 h-3.5 mr-1" />Download All (ZIP)
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200/70" data-testid="bulk-custom-rates">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <Pencil className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-semibold text-indigo-700">Custom Rates (all customers):</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="w-11"></span>
                          <span className="text-[10px] text-slate-500 w-20 text-center">Peak c/kWh</span>
                          <span className="text-[10px] text-slate-500 w-20 text-center">Shoulder c/kWh</span>
                          <span className="text-[10px] text-slate-500 w-20 text-center">Off-Pk c/kWh</span>
                        </div>
                        {[['1', 'Year 1'], ['2', 'Year 2'], ['3', 'Year 3']].map(function(y) {
                          return (
                            <div key={y[0]} className="flex items-center gap-1 mb-1">
                              <span className="text-[10px] font-semibold text-indigo-700 w-11">{y[1]}</span>
                              {['peak', 'shoulder', 'offpeak'].map(function(s) {
                                var f = 'y' + y[0] + '_' + s;
                                return <Input key={s} className="h-7 text-xs w-20" value={bulkRates[f] || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n[f] = v; return n; }); }} data-testid={'bulk-rate-' + f} />;
                              })}
                            </div>
                          );
                        })}
                      </div>
                      {[['metering_monthly', 'Metering $/mth'], ['service_monthly', 'Service $/mth']].map(function(f) {
                        return (
                          <div key={f[0]} className="flex flex-col">
                            <span className="text-[10px] text-slate-500">{f[1]}</span>
                            <Input className="h-7 text-xs w-24" value={bulkRates[f[0]] || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n[f[0]] = v; return n; }); }} data-testid={'bulk-rate-' + f[0]} />
                          </div>
                        );
                      })}
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-500">Quote Valid Until</span>
                        <Input type="date" className="h-7 text-xs w-32" value={bulkRates.quote_valid_until || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n.quote_valid_until = v; return n; }); }} data-testid="bulk-rate-quote_valid_until" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-500">Contract Start Date</span>
                        <Input type="date" className="h-7 text-xs w-32" value={bulkRates.start_date || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n.start_date = v; return n; }); }} data-testid="bulk-rate-start_date" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] text-slate-500">Solar Size (kW)</span>
                        <Input className="h-7 text-xs w-24" placeholder="e.g. 99.9" value={bulkRates.solar_size || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n.solar_size = v; return n; }); }} data-testid="bulk-rate-solar_size" />
                      </div>
                      <div className="flex flex-col">
                        <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                          <input type="checkbox" className="w-3.5 h-3.5 accent-indigo-600" checked={bulkRates.has_battery === 'yes'} onChange={function(e) { var v = e.target.checked ? 'yes' : ''; setBulkRates(function(p) { var n = {...p}; n.has_battery = v; return n; }); }} data-testid="bulk-rate-has_battery" />
                          Battery
                        </label>
                        <Input className="h-7 text-xs w-24" placeholder="Size kW" disabled={bulkRates.has_battery !== 'yes'} value={bulkRates.battery_size || ''} onChange={function(e) { var v = e.target.value; setBulkRates(function(p) { var n = {...p}; n.battery_size = v; return n; }); }} data-testid="bulk-rate-battery_size" />
                      </div>
                      <Button size="sm" variant="outline" onClick={applyRatesToAll} disabled={applyingBulkRates} className="border-indigo-500 text-indigo-700 hover:bg-indigo-50 self-end" data-testid="apply-rates-all-btn">
                        {applyingBulkRates ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Pencil className="w-3.5 h-3.5 mr-1" />}
                        Apply to All
                      </Button>
                    </div>
                    <div className="flex items-center gap-5 flex-wrap mt-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-indigo-700">Term:</span>
                        {['12', '24', '36'].map(function(t) {
                          var isActive = bulkRates.term_months === t;
                          return <button key={t} onClick={function() { setBulkRates(function(p) { return {...p, term_months: isActive ? '' : t}; }); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'bulk-term-' + t}>
                            {t} Months
                          </button>;
                        })}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-indigo-700">Connection Type:</span>
                        {[['renewal', 'Renewal'], ['move_in', 'Move in'], ['transfer', 'Transfer']].map(function(ct) {
                          var isActive = bulkRates.connection_type === ct[0];
                          return <button key={ct[0]} onClick={function() { setBulkRates(function(p) { return {...p, connection_type: isActive ? '' : ct[0]}; }); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'bulk-conn-' + ct[0]}>
                            {ct[1]}
                          </button>;
                        })}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Custom rates override the rate sheet for energy, metering &amp; service. Environmental charges always come from the rate sheet. All values ex-GST. Term &amp; connection tick the boxes on the contract.</p>
                  </div>
                </CardContent>
              </Card>

              {visibleItems().map(function(item) {
                var grp = item.grp;
                var idx = item.idx;
                var isExpanded = expandedGroups[idx];
                var pdfGenerated = batchDetail.generated_pdfs && batchDetail.generated_pdfs[String(idx)];
                return (
                  <Card key={idx} className="overflow-hidden" data-testid={'group-card-' + idx}>
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={function() { toggleGroup(idx); }}>
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-[#005394] flex-shrink-0" />
                            <h3 className="text-sm font-bold text-slate-800 truncate">{grp.company_name}</h3>
                            <Badge variant="outline" className="text-xs flex-shrink-0">{stateLabel(grp.contract_state)}</Badge>
                            {grp.row_count > 1 && <Badge className="bg-amber-100 text-amber-800 text-xs flex-shrink-0">{grp.row_count} NMIs</Badge>}
                            {grp.proposed_transfer_date && <Badge className="bg-orange-100 text-orange-800 text-xs flex-shrink-0 flex items-center gap-1" data-testid={'expiry-badge-' + idx}><CalendarClock className="w-3 h-3" />Expiry: {grp.proposed_transfer_date}</Badge>}
                            {pdfGenerated && <Badge className="bg-green-100 text-green-800 text-xs flex-shrink-0">PDF Ready</Badge>}
                            {batchDetail.custom_rates && batchDetail.custom_rates[String(idx)] && <Badge className="bg-indigo-100 text-indigo-800 text-xs flex-shrink-0" data-testid={'custom-rates-badge-' + idx}>Custom Rates</Badge>}
                            {batchDetail.emailed && batchDetail.emailed[String(idx)] && <Badge className="bg-blue-100 text-blue-800 text-xs flex-shrink-0">Emailed</Badge>}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            {grp.abn && <span>ABN: {grp.abn}</span>}
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{grp.supply_addresses[0] ? grp.supply_addresses[0].suburb + ' ' + grp.supply_addresses[0].state : ''}</span>
                            {grp.contact_name && <span>{grp.contact_name}</span>}
                          </div>
                          {(grp.sales_user || grp.sales_channel) && (
                            <div className="flex items-center gap-3 text-xs mt-0.5">
                              {grp.sales_user && <span className="text-slate-500">Sold by: <span className="font-medium text-slate-700">{grp.sales_user}</span></span>}
                              {grp.sales_channel && <span className="text-slate-500">Channel: <span className="font-medium text-slate-700">{grp.sales_channel}</span></span>}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <Button size="sm" onClick={function() { generatePdf(batchDetail.id, idx); }} disabled={generating[idx]} className="bg-[#005394] hover:bg-[#005394]/90" data-testid={'generate-pdf-' + idx}>
                            {generating[idx] ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                            {generating[idx] ? 'Generating...' : 'Generate PDF'}
                          </Button>
                          {pdfGenerated && (
                            <Button size="sm" variant="outline" onClick={function() { downloadPdf(batchDetail.id, idx, grp); }} data-testid={'download-pdf-' + idx}>
                              <Download className="w-3.5 h-3.5 mr-1" />Download
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={function() { emailContract(batchDetail.id, idx, grp); }} disabled={emailing[idx]} className="border-green-600 text-green-700 hover:bg-green-50" data-testid={'email-btn-' + idx}>
                            {emailing[idx] ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                            {emailing[idx] ? 'Sending...' : 'Email'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={function() { handleMailto(batchDetail.id, idx, grp); }} className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid={'mailto-btn-' + idx}>
                            <Mail className="w-3.5 h-3.5 mr-1" />Mail
                          </Button>
                          <button onClick={function() { toggleGroup(idx); }} className="p-1 text-slate-400 hover:text-slate-600">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          {/* Commission */}
                          <div className="mb-3 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200" data-testid={'commission-section-' + idx}>
                            <div className="flex items-center gap-3 flex-wrap">
                              <DollarSign className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                              <Label className="text-xs font-semibold text-emerald-800 flex-shrink-0">Commission</Label>
                              <div className="flex gap-1.5">
                                {[['percent', '%'], ['ckwh', 'c/kWh']].map(function(ct) {
                                  var isActive = (commissions[idx] || {}).type === ct[0];
                                  return <button key={ct[0]} onClick={function() { updateCommission(idx, 'type', ct[0]); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'comm-type-' + ct[0] + '-' + idx}>
                                    {ct[1]}
                                  </button>;
                                })}
                              </div>
                              <Input className="h-7 text-xs w-20" value={(commissions[idx] || {}).value || ''} onChange={function(e) { updateCommission(idx, 'value', e.target.value); }} placeholder="e.g. 5" data-testid={'comm-value-' + idx} />
                              {(commissions[idx] || {}).type && (commissions[idx] || {}).value && (
                                <span className="text-xs text-emerald-600 font-medium">+{(commissions[idx] || {}).value}{(commissions[idx] || {}).type === 'percent' ? '%' : ' c/kWh'}</span>
                              )}
                              {batchDetail && batchDetail.commissions && batchDetail.commissions[String(idx)] && (
                                <Badge className="bg-emerald-100 text-emerald-800 text-xs">Last: {batchDetail.commissions[String(idx)].value}{batchDetail.commissions[String(idx)].type === 'percent' ? '%' : ' c/kWh'}</Badge>
                              )}
                            </div>
                          </div>
                          {/* Custom Rates */}
                          <div className="mb-3 p-2.5 bg-indigo-50 rounded-lg border border-indigo-200" data-testid={'custom-rates-section-' + idx}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <Pencil className="w-4 h-4 text-indigo-700 flex-shrink-0" />
                              <Label className="text-xs font-semibold text-indigo-800">Custom Rates {batchDetail.custom_rates && batchDetail.custom_rates[String(idx)] ? '(active — overrides rate sheet)' : '(optional — rate sheet used if blank)'}</Label>
                            </div>
                            <div className="flex items-start gap-5 flex-wrap">
                              <div>
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span className="w-11"></span>
                                  <span className="text-[10px] text-slate-500 w-20 text-center">Peak c/kWh</span>
                                  <span className="text-[10px] text-slate-500 w-20 text-center">Shoulder c/kWh</span>
                                  <span className="text-[10px] text-slate-500 w-20 text-center">Off-Pk c/kWh</span>
                                </div>
                                {[['1', 'Year 1'], ['2', 'Year 2'], ['3', 'Year 3']].map(function(y) {
                                  return (
                                    <div key={y[0]} className="flex items-center gap-1 mb-1">
                                      <span className="text-[10px] font-semibold text-indigo-700 w-11">{y[1]}</span>
                                      {['peak', 'shoulder', 'offpeak'].map(function(s) {
                                        var f = 'y' + y[0] + '_' + s;
                                        return <Input key={s} className="h-7 text-xs w-20 bg-white" value={(customRates[idx] || emptyRates)[f] || ''} onChange={function(e) { updateCustomRate(idx, f, e.target.value); }} data-testid={'rate-' + f + '-' + idx} />;
                                      })}
                                    </div>
                                  );
                                })}
                                <p className="text-[10px] text-slate-400">Leave a year blank to leave it empty on the contract.</p>
                              </div>
                              <div className="flex items-end gap-2 flex-wrap">
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-slate-500">Metering $/mth</span>
                                  <Input className="h-7 text-xs w-24 bg-white" value={(customRates[idx] || emptyRates).metering_monthly || ''} onChange={function(e) { updateCustomRate(idx, 'metering_monthly', e.target.value); }} data-testid={'rate-metering_monthly-' + idx} />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-slate-500">Service $/mth</span>
                                  <Input className="h-7 text-xs w-24 bg-white" value={(customRates[idx] || emptyRates).service_monthly || ''} onChange={function(e) { updateCustomRate(idx, 'service_monthly', e.target.value); }} data-testid={'rate-service_monthly-' + idx} />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-slate-500">Quote Valid Until</span>
                                  <Input type="date" className="h-7 text-xs w-32 bg-white" value={(customRates[idx] || emptyRates).quote_valid_until || ''} onChange={function(e) { updateCustomRate(idx, 'quote_valid_until', e.target.value); }} data-testid={'rate-quote_valid_until-' + idx} />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-slate-500">Contract Start Date</span>
                                  <Input type="date" className="h-7 text-xs w-32 bg-white" value={(customRates[idx] || emptyRates).start_date || ''} onChange={function(e) { updateCustomRate(idx, 'start_date', e.target.value); }} data-testid={'rate-start_date-' + idx} />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-slate-500">Solar Size (kW)</span>
                                  <Input className="h-7 text-xs w-24 bg-white" placeholder="e.g. 99.9" value={(customRates[idx] || emptyRates).solar_size || ''} onChange={function(e) { updateCustomRate(idx, 'solar_size', e.target.value); }} data-testid={'rate-solar_size-' + idx} />
                                </div>
                                <div className="flex flex-col">
                                  <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                                    <input type="checkbox" className="w-3.5 h-3.5 accent-indigo-600" checked={(customRates[idx] || emptyRates).has_battery === 'yes'} onChange={function(e) { updateCustomRate(idx, 'has_battery', e.target.checked ? 'yes' : ''); }} data-testid={'rate-has_battery-' + idx} />
                                    Battery
                                  </label>
                                  <Input className="h-7 text-xs w-24 bg-white" placeholder="Size kW" disabled={(customRates[idx] || emptyRates).has_battery !== 'yes'} value={(customRates[idx] || emptyRates).battery_size || ''} onChange={function(e) { updateCustomRate(idx, 'battery_size', e.target.value); }} data-testid={'rate-battery_size-' + idx} />
                                </div>
                                <Button size="sm" onClick={function() { saveCustomRates(idx); }} disabled={savingRates[idx]} className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs" data-testid={'save-rates-' + idx}>
                                  {savingRates[idx] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save Rates
                                </Button>
                                {batchDetail.custom_rates && batchDetail.custom_rates[String(idx)] && (
                                  <Button size="sm" variant="outline" onClick={function() { clearCustomRates(idx); }} className="h-7 text-xs border-slate-300 text-slate-600" data-testid={'clear-rates-' + idx}>
                                    <X className="w-3 h-3 mr-1" />Use Rate Sheet
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="text-[10px] text-indigo-500 mt-1">Overrides energy, metering &amp; service pricing on the contract. Environmental charges still come from the rate sheet. Ex-GST.</p>
                            <div className="flex items-center gap-5 flex-wrap mt-2 pt-2 border-t border-indigo-100">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold text-indigo-700">Term:</span>
                                {['12', '24', '36'].map(function(t) {
                                  var isActive = (customRates[idx] || emptyRates).term_months === t;
                                  return <button key={t} onClick={function() { updateCustomRate(idx, 'term_months', isActive ? '' : t); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'term-' + t + '-' + idx}>
                                    {t} Months
                                  </button>;
                                })}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold text-indigo-700">Connection Type:</span>
                                {[['renewal', 'Renewal'], ['move_in', 'Move in'], ['transfer', 'Transfer']].map(function(ct) {
                                  var isActive = (customRates[idx] || emptyRates).connection_type === ct[0];
                                  return <button key={ct[0]} onClick={function() { updateCustomRate(idx, 'connection_type', isActive ? '' : ct[0]); }} className={'text-xs px-2 py-1 rounded border transition-colors ' + (isActive ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'conn-' + ct[0] + '-' + idx}>
                                    {ct[1]}
                                  </button>;
                                })}
                              </div>
                              <span className="text-[10px] text-slate-400">Ticks the offer &amp; connection boxes on the contract. Default connection: Renewal.</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-3">
                            <div><span className="text-slate-400">Company:</span> <span className="text-slate-700 font-medium">{grp.company_name}</span></div>
                            <div><span className="text-slate-400">Type:</span> <span className="text-slate-700">{grp.customer_type}</span></div>
                            <div><span className="text-slate-400">ABN:</span> <span className="text-slate-700">{grp.abn || '-'}</span></div>
                            <div><span className="text-slate-400">Contact:</span> <span className="text-slate-700">{grp.contact_name || '-'}</span></div>
                            <div><span className="text-slate-400">Phone:</span> <span className="text-slate-700">{grp.phone || '-'}</span></div>
                            <div><span className="text-slate-400">Mobile:</span> <span className="text-slate-700">{grp.mobile || '-'}</span></div>
                            <div><span className="text-slate-400">Email:</span> <span className="text-slate-700">{grp.email || '-'}</span></div>
                            <div><span className="text-slate-400">Billing:</span> <span className="text-slate-700">{[grp.billing_address, grp.billing_suburb, grp.billing_state, grp.billing_postcode].filter(Boolean).join(', ')}</span></div>
                            <div><span className="text-slate-400">Sold by:</span> <span className="text-slate-700">{grp.sales_user || '-'}</span></div>
                            <div><span className="text-slate-400">Sales Channel:</span> <span className="text-slate-700">{grp.sales_channel || '-'}</span></div>
                            <div><span className="text-slate-400">Current Expiry:</span> <span className="text-orange-700 font-medium">{grp.proposed_transfer_date || '-'}</span></div>
                          </div>
                          <h4 className="text-xs font-bold text-[#005394] mb-2">Supply Addresses ({grp.supply_addresses.length})</h4>
                          <table className="w-full text-xs border-collapse" data-testid={'supply-table-' + idx}>
                            <thead><tr className="bg-[#005394] text-white"><th className="p-1.5 text-left">Address</th><th className="p-1.5">Suburb</th><th className="p-1.5">State</th><th className="p-1.5">NMI</th><th className="p-1.5">Tariff</th><th className="p-1.5">Est kWh</th></tr></thead>
                            <tbody>
                              {grp.supply_addresses.map(function(sa, si) {
                                return (
                                  <tr key={si} className={si % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                    <td className="p-1.5">{sa.supply_address}</td>
                                    <td className="p-1.5 text-center">{sa.suburb}</td>
                                    <td className="p-1.5 text-center">{sa.state}</td>
                                    <td className="p-1.5 text-center font-mono">{sa.nmi}</td>
                                    <td className="p-1.5 text-center">{sa.tariff}</td>
                                    <td className="p-1.5 text-center">{sa.estimated_kwh}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    <AlertDialog open={!!confirmDeleteBatch} onOpenChange={function(open) { if (!open) setConfirmDeleteBatch(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Batch</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete this batch and all generated PDFs? This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDeleteBatchAction} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </Layout>
  );
}
